"use client";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { registerSuperProperties } from "~/lib/analytics";
import { Button } from "../ui/button";

const BANNER_DISMISSED_KEY = "ezspot_cookie_banner_dismissed";

// Routes that render a full-viewport overlay and lock body scroll, plus
// `/messages`: `MessagesPageShell` is a fixed `100dvh - var(--nav-h)`,
// `overflow-hidden` frame with no page-level scroll for a `fixed` banner to
// push — it would paint straight over the composer instead, blocking the
// page's primary action for anyone who hasn't dismissed cookies yet.
const EXCLUDE_BANNER_ROUTES_REGEX =
  /^\/(host\/listings\/(new|[^/]+\/(details|pricing|availability|reservations))|messages(\/|$))/;

export function Banner() {
  const pathname = usePathname();
  const posthog = usePostHog();
  const [hydrated, setHydrated] = useState(false);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setDismissed(window.localStorage.getItem(BANNER_DISMISSED_KEY) === "true");
    setHydrated(true);
  }, []);

  const dismissBanner = () => {
    window.localStorage.setItem(BANNER_DISMISSED_KEY, "true");
    setDismissed(true);
  };

  // Both handlers re-register unconditionally. Crossing the cookieless
  // boundary resets the instance to keep cookied and cookieless state from
  // leaking into each other, which clears the super properties, and nothing
  // here reloads the page. Opting out always crosses it; opting in only
  // crosses it for an already-rejected visitor, who should not be able to
  // reach this banner at all — but that rests on PostHog's consent record and
  // our own dismissed flag never diverging, and the call is idempotent, so
  // there is no reason to depend on that.

  const handleAcceptCookies = () => {
    // full cookied capture.
    posthog.opt_in_capturing();
    registerSuperProperties(posthog);
    dismissBanner();
  };

  const handleDeclineCookies = () => {
    // Stays in cookieless capture.
    posthog.opt_out_capturing();
    registerSuperProperties(posthog);
    dismissBanner();
  };

  if (!hydrated || dismissed) return null;
  if (EXCLUDE_BANNER_ROUTES_REGEX.test(pathname)) return null;

  return (
    <section className="fixed inset-x-0 bottom-0 z-50 border-t border-(--ink-10) bg-white shadow-(--shadow-raised)">
      <div className="mx-auto flex max-w-7xl gap-4 flex-col md:flex-row  sm:items-center p-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="min-w-0">
            <p className="mb-1 text-(--ink-40)">Cookie preferences</p>
            <p className="text-(--ink-60)">
              We use tracking cookies to understand how you use the product and
              help us improve it. Please accept cookies to help us improve.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="ghost" size="sm" onClick={handleDeclineCookies}>
            Decline
          </Button>
          <Button variant="primary" size="sm" onClick={handleAcceptCookies}>
            Accept cookies
          </Button>
        </div>
      </div>
    </section>
  );
}
